import {Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import {StateParamsModel} from 'src/app/modules/common/Model'
import * as user from 'src/app/modules/userMgmt'
import * as departmentRedux from 'src/app/modules/userMgmt/components/departments'
import {useGetBranch} from 'src/app/queries/location/branches'
import useGetProvinceOptions from 'src/app/queries/location/province'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import {useGetRoleOptions} from 'src/cms/hooks/get/useGetRoleOptions'

import * as Yup from 'yup'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

interface Province {
  id: string
  title: string
  titleNp: string
  code: number
}

export interface User {
  name: string
  branch_id: string
  userName: string
  employee_service_code: string
  email: string
  mobile_number: string
  departmentId: string
  grievance_manager: boolean
  vendor: boolean
  provinces: string | string[] | {id: string}[]
  branches: string | string[] | {id: string}[]
}

const FORM_VALIDATION = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .matches(/^[a-zA-Z0-9_ .]+$/, 'Must not contain symbols except dot (.)'),

  userName: Yup.string()
    .required('Username is required')
    .matches(/^[a-zA-Z0-9_ .]+$/, 'Must not contain symbols except dot (.)'),

  employee_service_code: Yup.string()
    .required('User ID is required')
    .matches(/^[a-zA-Z0-9_-]+$/, 'Must not contain symbols except hyphen (-) and underscore (_)'),

  email: Yup.string()
    .required('Email is required')
    .email('Please provide valid email')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Please provide valid email'),
  mobile_number: Yup.string()
    .required('Mobile No. is required')
    .matches(/^(98|97)([0-9]{8})$/, 'Enter valid phone number')
    .max(10, 'Phone number should not exceed 10 digits'),
  branch_id: Yup.string().required('Main Branch is required.'),
  branches: Yup.array()
    .min(1, 'At least one branch must be selected')
    .of(Yup.string().required('Branch is required'))
    .required('Please select at least one branch'),
  designation: Yup.string().required('Designation is required'),
})

export interface IOption {
  label: string
  value: string
}

const AddUser = ({open, params, handleClose, actionType, editSelectedData}: Props) => {
  const {data: designationOptions, isLoading, isError} = useGetRoleOptions()

  const dispatch = useDispatch()
  const {loading, success} = useSelector((state: any) => state.userManagement)
  const {data: provinceOptions} = useGetProvinceOptions()

  const [branchParams, setBranchParams] = useState({
    search: '',
    page: 1,
    limit: 200,
    orderBy: '',
    order: '',
  })

  const {data: branchOption} = useGetBranch(branchParams)

  const {
    data: {department},
  } = useSelector((state: any) => state.departments)

  useEffect(() => {
    dispatch(departmentRedux.actions.getDepartment())
  }, [])

  const departmentOptions = department.map((items: {[key: string]: string}) => ({
    label: items.name,
    value: items.id,
  }))

  useEffect(() => {
    if (success) {
      dispatch(user?.actions.getUsers(params))
      isEmpty(editSelectedData)
        ? toast.success('User added successfully')
        : toast.success('User edited successfully')
      dispatch(user?.actions?.resetUsers())
      handleClose()
    }
  }, [success])

  return (
    <div className='modal-container' data-cy='modal-user-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} User </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={{
                name: '',
                userName: '',
                employee_service_code: '',
                email: '',
                mobile_number: '',
                departmentId: '',
                grievance_manager: false,
                vendor: false,
                provinces: [],
                branches: [],
                branch_id: '',
                designation: '',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => {
                const selectedDesignation = designationOptions?.find(
                  (opt: any) => opt.value === values.designation
                )

                const payload = {
                  ...values,
                  designation: selectedDesignation?.label,
                  provinces: values?.provinces?.map((province) => ({id: province})),
                  branches: values?.branches?.map((branch) => ({id: branch})),
                }

                if (!isEmpty(editSelectedData)) {
                  dispatch(user.actions.updateUsers(payload, editSelectedData?.id))
                } else {
                  dispatch(user.actions.addUsers(payload))
                }
              }}
            >
              {({touched, handleChange, errors, setFieldValue, values}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    const initialProvinces = editSelectedData?.provinces?.map(
                      (item: any) => item.province_id
                    )
                    const defaultBranchId = editSelectedData?.branch_id || ''
                    const initialBranches =
                      Array.isArray(editSelectedData?.branches) &&
                      editSelectedData?.branches.length > 0
                        ? editSelectedData.branches.map((item: any) => item?.id)
                        : []

                    const updatedBranches = initialBranches.includes(defaultBranchId)
                      ? initialBranches
                      : [...initialBranches, defaultBranchId]

                    const fields = [
                      'name',
                      'userName',
                      'employee_service_code',
                      'email',
                      'mobile_number',
                      'departmentId',
                      'grievance_manager',
                      'vendor',
                      'provinces',
                      'branches',
                      'branch_id',
                      'designation',
                    ]
                    fields.forEach((field) => setFieldValue(field, editSelectedData[field], false))
                    setFieldValue('provinces', initialProvinces, false)
                    setFieldValue('branches', updatedBranches, false)
                    setFieldValue('branch_id', editSelectedData?.branch_id, false)
                    const matchedDesignation = designationOptions?.find(
                      (opt: any) => opt.label === editSelectedData.designation
                    )
                    setFieldValue('designation', matchedDesignation?.value, false)
                  }
                }, [provinceOptions, designationOptions])

                useEffect(() => {
                  const allBranchIds =
                    branchOption?.data?.branch?.map((item: any) => item?.id) || []

                  const onlyAllSelected =
                    values?.branches?.length === 1 && values?.branches[0] === 'all'

                  if (onlyAllSelected) {
                    setFieldValue('branches', allBranchIds, false)
                  }
                }, [values?.branches, branchOption])

                return (
                  <Form>
                    <div className='card-body border-top pt-5' data-cy='card-user-body'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            containerClassName='col-md-12'
                            type='text'
                            placeholder='Full Name'
                            label='Name'
                            name='name'
                            disabled={!isEmpty(editSelectedData) ? true : false}
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            data-cy='add-user-full-name-text-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            containerClassName='col-md-12'
                            type='text'
                            placeholder='Username'
                            label='Username'
                            name='userName'
                            disabled={!isEmpty(editSelectedData) ? true : false}
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            data-cy='add-user-name-text-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            containerClassName='col-md-12'
                            type='text'
                            placeholder='User ID'
                            label='User ID'
                            name='employee_service_code'
                            // disabled={!isEmpty(editSelectedData) ? true : false}
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            data-cy='add-user-id-text-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            containerClassName='col-md-12'
                            type='email'
                            placeholder='Email'
                            name='email'
                            disabled={!isEmpty(editSelectedData) ? true : false}
                            label='Email'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            value={values?.email}
                            data-cy='add-user-name-email-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12 pb-0 mb-0'>
                          <FormTextBox
                            containerClassName='col-12'
                            type='text'
                            placeholder='Enter Mobile Number'
                            label='Mobile Number'
                            name='mobile_number'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required={true}
                            data-cy='add-user-number-text-field'
                          />
                          <div className='d-flex justify-content-end'>
                            <em>
                              <small className='d-block'>
                                <span className='text-danger' data-cy='text-user-danger'>
                                  Note:{' '}
                                </span>{' '}
                                Mobile number should starts with 98|97
                              </small>
                            </em>
                          </div>
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select'
                            label='Department'
                            name='departmentId'
                            isClearable={true}
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={departmentOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                            arrValue={
                              !isEmpty(editSelectedData) && !values.departmentId
                                ? editSelectedData?.departmentId
                                : values.departmentId
                            }
                            data-cy='add-user-department-dropdown-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select'
                            label='Main Branch'
                            name='branch_id'
                            isClearable={true}
                            errors={errors}
                            touched={touched}
                            options={
                              branchOption?.data?.branch?.map((item: any) => ({
                                label: item?.title,
                                value: item?.id,
                              })) || []
                            }
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select'
                            label='Branches'
                            name='branches'
                            isClearable={true}
                            multiple={true}
                            errors={errors}
                            touched={touched}
                            options={[
                              {label: 'All', value: 'all'},
                              ...(branchOption?.data?.branch?.map((item: any) => ({
                                label: item?.title,
                                value: item?.id,
                              })) || []),
                            ]}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Please select'
                            label='Designation'
                            name='designation'
                            isClearable={true}
                            errors={errors}
                            touched={touched}
                            options={designationOptions}
                            values={values}
                            required={true}
                            setFieldValue={setFieldValue}
                            data-cy='add-user-designation-dropdown-field'
                          />
                        </div>

                        <div className='d-flex justify-content-end px-9 '>
                          <button
                            type='submit'
                            disabled={loading}
                            className='btn btn-primary btn-sm ms-3'
                            data-cy='modal-submit'
                          >
                            Save
                          </button>
                          <button
                            type='button'
                            onClick={handleClose}
                            className='btn btn-secondary btn-sm ms-3'
                            data-cy='modal-cancel'
                          >
                            Cancel
                          </button>
                        </div>
                      </div>{' '}
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AddUser